<script setup lang="ts">
import AppGrid from "@/components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "@/components/composables/useGridColumnDefaults.ts";
import ItemMovementDetailModal from "@/features/results/ItemMovementDetailModal.vue";
import type { MovementGridRow } from "@/redistribution/models/MovementGridRow.ts";
import { useRedistributionStore } from "@/stores/redistributionStore.ts";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useItemMovementsGridColumns } from "./composables/useItemMovementsGridColumns.ts";
const { itemMovementsGridRowData, updatedPOs } = storeToRefs(
  useRedistributionStore()
);

const rowData = computed(() => itemMovementsGridRowData.value);
const showDetailModal = ref(false);
const selectedMovement = ref<MovementGridRow | null>(null);

function handleRowClick(event: RowClickedEvent<MovementGridRow>): void {
  selectedMovement.value = event.data ?? null;
  showDetailModal.value = true;
}

const columnDefs = useItemMovementsGridColumns();

const defaultColDef: ColDef = FITTABLE_DEFAULT_COL_DEF;

function getRowHeight(): number {
  return 52;
}
</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col">
    <p class="item-movements-caption shrink-0">
      Only items that moved between POs are shown.
    </p>
    <div v-if="rowData.length === 0" class="panel-empty-state">
      <h3 class="mb-2 text-lg text-slate-700">No Movements</h3>
      <p class="text-sm">Items were already optimally distributed.</p>
    </div>
    <AppGrid
      v-else
      id="item-movements-grid"
      class="flex-1"
      :column-defs="columnDefs"
      :row-data="rowData"
      :default-col-def="defaultColDef"
      :get-row-height="getRowHeight"
      :fill-container="true"
      show-top-border
      :no-rows-overlay-component-params="{
        getTitle: () => {
          return 'No Item Movements to Display';
        },
      }"
      @row-clicked="handleRowClick"
    />
  </div>

  <ItemMovementDetailModal
    :is-open="showDetailModal"
    :movement="selectedMovement"
    :movements="rowData"
    :updated-pos="updatedPOs"
    @close="showDetailModal = false"
  />
</template>
